import { CompanyType } from "../infrastructure/enums/company-type.enum";
import { InventoryStatus } from "../infrastructure/enums/meta-data-field.enum";

export class HistoryLog {
   companyType: CompanyType;
   companyKey: string = '';
   key: string = '';
   eventType: EventType;
   entityType: EntityType;
   entityKey: string = '';
   changes: Object = {};
   context: Object = {};
   triggeredByUserId: string = '';
   triggeredBySystem: string = '';
   timestamp: Date;

  constructor(init?: Partial<HistoryLog>) {
    if (init) {
      //if no values are passed in, fresh instantiation
      Object.assign(this, init);
    }
  }
}

export interface FieldChange<T = any> {
  old: T;
  new: T;
}

/**
 * CONFIGURATION: LogDefinition
 * This is the "Named Parameter" pattern.
 * Instead of passing 3 random generics, you define a structure that fits this shape.
 */
export interface LogDefinition {
  Changes: Record<string, FieldChange>; // What changed?
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  Params: any; // Input context
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  Decision: any; // Output decision
}

export interface LogPayload<Def extends LogDefinition> {
  changes: Def["Changes"]; // The delta/diff (JSONB)
  context: {
    params: Def["Params"];
    decision: Def["Decision"];
  }; // The context (JSONB)
}

export interface CampaignBudgetChangesLog
  extends LogPayload<{
    Changes: {
      budget: FieldChange<number>;
    };
    Params: {
      min_days_until_stockout_risk: number | null;
      max_days_until_stockout_risk: number | null;
      avg_days_until_stockout: number;
      budget: number;
      suggest_budget: number;
      new_budget: number;
    };
    Decision: {
      new_budget: number;
      inventory_status: InventoryStatus;
    };
  }> {}

type PriceFieldChanges = FieldChange<number | undefined>;

export interface ItemPricingChangesByRecommendationLog
  extends LogPayload<{
    Changes: {
      inbound_price: PriceFieldChanges;
      business_price: PriceFieldChanges;
      sale_price?: PriceFieldChanges;
      min_price?: PriceFieldChanges;
      max_price?: PriceFieldChanges;
    };
    Params: {
      restock_days: number;
      restock_date: string;
      velocity: number;
      days_of_inventory: number;
    };
    Decision: {
      risk_ratio?: number;
      state: ItemPriceState;
      suggested_price?: number;
      custom_price?: number;
    };
  }> {}

export enum EntityType {
  CAMPAIGN = 'CAMPAIGN',
  ITEM = 'ITEM',
}

export enum EventType {
  BUDGET_CHANGES = 'BUDGET_CHANGES',
  PRICING_CHANGES_BY_RECOMMENDATION = "PRICING_CHANGES_BY_RECOMMENDATION",
  PRICING_CHANGES_BY_SYNCED = "PRICING_CHANGES_BY_SYNCED",
}

export enum ItemPriceState {
  // --- Edge Cases (Priority High) ---
  MISSING_PRICE = 'MISSING_PRICE',
  DEAD_STOCK = 'DEAD_STOCK', // Was: No sales & No inventory
  NO_DEMAND = 'NO_DEMAND', // Was: No sales
  STOCK_OUT_HIGH_DEMAND = 'STOCK_OUT_HIGH_DEMAND', // Was: Out of stock (increase price)
  STOCK_OUT_RESTOCKING = 'STOCK_OUT_RESTOCKING', // Was: Restock imminent

  // --- Ratio/Inventory Based (Priority Low) ---
  OVERSTOCK_CRITICAL = 'OVERSTOCK_CRITICAL', // Ratio >= 3
  OVERSTOCK_HIGH = 'OVERSTOCK_HIGH', // Ratio >= 1.5
  BALANCED = 'BALANCED', // Ratio >= 1.0
  UNDERSTOCK_MILD = 'UNDERSTOCK_MILD', // Ratio >= 0.75
  UNDERSTOCK_MODERATE = 'UNDERSTOCK_MODERATE', // Ratio >= 0.5
  UNDERSTOCK_CRITICAL = 'UNDERSTOCK_CRITICAL', // Ratio < 0.5
}

export interface LogRegistry {
  [EntityType.CAMPAIGN]: {
    [EventType.BUDGET_CHANGES]: CampaignBudgetChangesLog;
  },
  [EntityType.ITEM]: {
    [EventType.PRICING_CHANGES_BY_RECOMMENDATION]: ItemPricingChangesByRecommendationLog;
  };
}

export type ValidEvents<E extends EntityType> = keyof LogRegistry[E];


